import { ValidationErrors } from '@angular/forms';
import {
  CarePlanPickerGoal,
  CarePlanPickerPlan,
  CarePlanWorkErrorCode,
  CarePlanWorkItemSnapshot,
  CarePlanWorkRecord,
  CarePlanWorkRequestPlan,
} from 'src/app/models/ccm/care-plan-work.model';
import { CarePlanAction, CarePlanTask } from 'src/app/models/ccm/ccm-data-model';

/** The same limits welby-micro-service enforces on one time entry (`CARE_PLAN_WORK_LIMITS`). */
export const CARE_PLAN_WORK_LIMITS = Object.freeze({
  plans: 10,
  goals: 30,
  noteLength: 500,
});

/** Keyed by the union, so a code added to `CarePlanWorkErrorCode` but not here does not compile. */
const ERROR_CODE_SET: Readonly<Record<CarePlanWorkErrorCode, true>> = {
  REQUIRED: true,
  NOTE_REQUIRED: true,
  NOTE_TOO_LONG: true,
  EMPTY_ITEM: true,
  TOO_MANY: true,
  MALFORMED: true,
  PLAN_NOT_ACTIVE: true,
  GOAL_NOT_OPEN: true,
  NO_INTERVENTIONS: true,
  PATIENT_CHANGED: true,
  INVALID_TIME_ID: true,
};

/** Every `code` welby-micro-service answers a refused CCM row with, to tell its 400s from any other. */
export const CARE_PLAN_WORK_ERROR_CODES: ReadonlyArray<CarePlanWorkErrorCode> = Object.freeze(
  Object.keys(ERROR_CODE_SET) as CarePlanWorkErrorCode[]
);

/** Code-unit order: the same in every browser, unlike `localeCompare`, which follows the user's locale. */
const byCodeUnit = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/**
 * Only `'closed'` closes a goal — the same rule welby-micro-service checks. The care plan form saves
 * `status: false` on every goal and never `true`, and the one screen that labels the field calls it
 * "Is open", so a boolean either way is not a reliable "closed".
 */
export const isOpenGoal = (goal: Pick<CarePlanAction, 'status'>): boolean => (goal?.status as unknown) !== 'closed';

/** The non-blank lines of a text, trimmed. A GLB intervention is often one numbered list in one field. */
export const toLines = (text: unknown): string[] =>
  typeof text === 'string'
    ? text
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
    : [];

/**
 * The lines of one intervention: its name, or — for a task with no name — its content, stored as a
 * string on some tasks and a list on others.
 */
const taskLines = (name: unknown, content: unknown): string[] => {
  const named = toLines(name);
  if (named.length) {
    return named;
  }
  return Array.isArray(content)
    ? content.reduce((all: string[], entry) => all.concat(toLines(entry)), [])
    : toLines(content);
};

/**
 * The interventions of a goal are the problem's `tasks` whose `cp_action_id` is the goal — not actions
 * with `action_type: 'intervention '`, which GLB's plans do not use.
 */
export const interventionLines = (tasks: CarePlanTask[], goalId: string): string[] =>
  (tasks ?? [])
    .filter((task) => task?.cp_action_id === goalId)
    .reduce((lines: string[], task) => lines.concat(taskLines(task.task_name, task.task_content)), []);

const frozenGoalOf = (item: CarePlanWorkItemSnapshot): CarePlanPickerGoal => ({
  problemId: item.problem_id,
  problemTitle: item.problem_title ?? '',
  goalId: item.goal_id,
  goalText: item.goal_text ?? '',
  interventions: (item.interventions_snapshot ?? []).reduce(
    (lines: string[], task) => lines.concat(taskLines(task.name, task.content)),
    []
  ),
  frozen: true,
  frozenGoal: item.goal === true,
  frozenInterventions: item.interventions === true,
});

/**
 * Adds to today's plans the goals an entry being edited was saved with but that are gone from today's
 * care plan — a goal deleted or closed, or a plan no longer Active. They are shown from the frozen copy,
 * so saving the edit keeps them; without this the picker would silently drop them.
 */
export const mergeFrozenPicks = (
  plans: CarePlanPickerPlan[],
  record: CarePlanWorkRecord | null
): CarePlanPickerPlan[] => {
  const merged = (plans ?? []).map((plan) => ({ ...plan, goals: [...plan.goals] }));
  (record?.plans ?? []).forEach((stored) => {
    const live = merged.find((plan) => plan.carePlanId === stored.care_plan_id);
    const missing = (stored.items ?? [])
      .filter((item) => !live?.goals.some((goal) => goal.problemId === item.problem_id && goal.goalId === item.goal_id))
      .map(frozenGoalOf);
    if (live) {
      // Next to the goals of its own problem, so the problem headings stay grouped.
      missing.forEach((goal) => {
        const lastOfProblem = live.goals.map((each) => each.problemId).lastIndexOf(goal.problemId);
        live.goals.splice(lastOfProblem >= 0 ? lastOfProblem + 1 : live.goals.length, 0, goal);
      });
    } else if (missing.length) {
      merged.push({
        carePlanId: stored.care_plan_id,
        name: stored.name ?? '',
        dxList: stored.dx_list ?? [],
        goals: missing,
        frozen: true,
      });
    }
  });
  return merged;
};

/**
 * Picks in a fixed order — plans by id, goals by problem then goal id — whatever the screen order. The
 * server skips writing a new record version when nothing changed, and compares arrays in order, so the
 * same picks must always come out the same way.
 */
export const sortRequestPlans = (plans: CarePlanWorkRequestPlan[]): CarePlanWorkRequestPlan[] =>
  [...plans]
    .map((plan) => ({
      ...plan,
      items: [...plan.items].sort((a, b) => byCodeUnit(a.problem_id, b.problem_id) || byCodeUnit(a.goal_id, b.goal_id)),
    }))
    .sort((a, b) => byCodeUnit(a.care_plan_id, b.care_plan_id));

/** The picks of a saved entry, as the time form starts from and sends back. */
export const toRequestPlans = (record: CarePlanWorkRecord | null): CarePlanWorkRequestPlan[] =>
  sortRequestPlans(
    (record?.plans ?? []).map((plan) => ({
      care_plan_id: plan.care_plan_id,
      note: plan.note ?? '',
      items: (plan.items ?? []).map((item) => ({
        problem_id: item.problem_id,
        goal_id: item.goal_id,
        goal: item.goal === true,
        interventions: item.interventions === true,
      })),
    }))
  );

/**
 * The picks whose goal is on screen, as the picker counts them: a pick left over from sections that
 * changed is not part of the row's work, and the picker drops it from the control on its next sync.
 */
export const visiblePicks = (
  plans: CarePlanPickerPlan[],
  value: CarePlanWorkRequestPlan[]
): CarePlanWorkRequestPlan[] =>
  (value ?? [])
    .map((picked) => {
      const plan = (plans ?? []).find((each) => each.carePlanId === picked.care_plan_id);
      return {
        ...picked,
        items: (picked.items ?? []).filter(
          (item) =>
            (item.goal || item.interventions) &&
            !!plan?.goals.some((goal) => goal.problemId === item.problem_id && goal.goalId === item.goal_id)
        ),
      };
    })
    .filter((picked) => picked.items.length > 0);

/**
 * The picker's validation, as a pure function so the time form can attach the same rule to a row's
 * control before the picker renders. With `required: false` — an entry saved as CCM before go live,
 * which the server lets be edited without care plan work (FR-5.3) — picking nothing is valid, but
 * anything picked must still carry its note.
 */
export const validateCarePlanWork = (
  plans: CarePlanPickerPlan[],
  value: CarePlanWorkRequestPlan[],
  { required }: { required: boolean }
): ValidationErrors | null => {
  const picked = value ?? [];
  if (!picked.length) {
    if (!required) {
      return null;
    }
    return (plans ?? []).length ? { carePlanWorkRequired: true } : { noActiveCarePlan: true };
  }
  // The limit first, as the server checks it: no point writing eleven notes to learn ten is the most.
  const goals = picked.reduce((count, plan) => count + plan.items.length, 0);
  if (picked.length > CARE_PLAN_WORK_LIMITS.plans || goals > CARE_PLAN_WORK_LIMITS.goals) {
    return { carePlanTooMany: true };
  }
  if (picked.some((plan) => !plan.note?.trim())) {
    return { carePlanNoteRequired: true };
  }
  if (picked.some((plan) => plan.note.trim().length > CARE_PLAN_WORK_LIMITS.noteLength)) {
    return { carePlanNoteTooLong: true };
  }
  return null;
};
